<template>
  <div class="product-list-page">
    <Create
      :createDialog="createDialog"
      @close="createDialog = !createDialog"
      @submit="getList"
    ></Create>
    <Edit
      :editDialog="editDialog"
      @close="editDialog = !editDialog"
      @submit="getList"
      :id="id"
    ></Edit>
    <ProductImagePreviewDialog
      v-model="imagePreviewDialog"
      :product="imagePreviewProduct"
      @image-error="repairPreviewImage"
    />
    <v-card :loading="loading" outlined class="product-card">
      <div class="product-header">
        <div>
          <div class="product-title">
            {{ $t("pageHeader.product") }}
          </div>
          <div class="product-subtitle">{{ totalItems }} products found</div>
        </div>

        <div class="product-actions">
          <v-btn
            v-if="checkRightStatus(1)"
            small
            rounded
            color="primary"
            dark
            class="new-product-button"
            @click="add"
          >
            <v-icon left small>mdi-plus</v-icon>
            <span class="product-action-label">New Product</span>
          </v-btn>
        </div>
      </div>

      <div class="product-filter-bar">
        <div class="filter-field search-field">
          <div class="filter-label">Search</div>
          <v-text-field
            v-model="search"
            outlined
            prepend-inner-icon="search"
            autofocus
            dense
            label="Product, UPC, SKU"
            single-line
            hide-details
            clearable
            class="product-search"
          />
        </div>

        <template v-if="$vuetify.breakpoint.mdAndUp">
          <div class="filter-field">
            <div class="filter-label">Department</div>
            <v-autocomplete
              :items="departmentList"
              @change="getList()"
              return-object
              item-text="name"
              clearable
              outlined
              dense
              label="All departments"
              v-model="selectedDepartment"
              hide-details
              class="filter-input"
            />
          </div>

          <div class="filter-field">
            <div class="filter-label">Brand</div>
            <v-autocomplete
              :items="brandList"
              @change="getList()"
              return-object
              item-text="name"
              clearable
              outlined
              dense
              label="All brands"
              v-model="selectedBrand"
              hide-details
              class="filter-input"
            />
          </div>

          <div class="filter-field">
            <div class="filter-label">Category</div>
            <v-autocomplete
              :items="categoryList"
              @change="getList()"
              return-object
              item-text="name"
              clearable
              outlined
              dense
              label="All categories"
              v-model="selectedCategory"
              hide-details
              class="filter-input"
            />
          </div>
          <div class="filter-field options-field">
            <div class="filter-label">&nbsp;</div>
            <v-btn
              small
              rounded
              outlined
              class="options-btn"
              @click="listOptionDialog = true"
            >
              <v-icon left small>mdi-tune</v-icon>
              Options
            </v-btn>
          </div>
        </template>

        <v-btn
          v-else
          icon
          class="product-filter-trigger"
          @click="mobileFilterDrawer = true"
        >
          <v-icon>mdi-tune-variant</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pa-4">
        <div v-if="bulkSelectionMode" class="bulk-selection-toolbar mb-3">
          <div>
            <div class="bulk-selection-title">Select products</div>
            <div class="bulk-selection-count">
              {{ selectedProducts.length }} selected
            </div>
          </div>

          <div class="bulk-selection-actions">
            <v-btn small rounded text @click="cancelBulkSelection">
              Cancel
            </v-btn>
            <v-btn
              small
              rounded
              color="primary"
              :disabled="!selectedProducts.length"
              @click="bulkAdjustDialog = true"
            >
              Adjust Selected
            </v-btn>
          </div>
        </div>

        <v-data-table
          v-model="selectedProducts"
          :show-select="bulkSelectionMode"
          item-key="id"
          :mobile-breakpoint="0"
          :headers="visibleHeaders"
          :items="products"
          :loading="loading"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :page.sync="page"
          :items-per-page.sync="itemsPerPage"
          :server-items-length="totalItems"
          :hide-default-footer="$vuetify.breakpoint.xsOnly"
          :single-expand="singleExpand"
          :expanded.sync="expanded"
          show-expand
          :footer-props="{ 'items-per-page-options': [10, 15, 25, 50, 100] }"
          class="product-table"
        >
          <template
            v-slot:item="{
              item,
              index,
              isExpanded,
              expand,
              isSelected,
              select,
            }"
          >
            <tr
              class="pointer"
              :class="productRowClass(item)"
            >
              <td v-if="bulkSelectionMode">
                <v-checkbox
                  :input-value="isSelected"
                  :disabled="!canBulkAdjust(item)"
                  hide-details
                  dense
                  class="ma-0 pa-0"
                  @change="select($event)"
                />
              </td>
              <td class="text-left product-name-cell">
                <v-avatar v-if="!item.fix && !item.isParent" size="5" color="red" />
                <v-btn
                  v-if="item.trackSerialNumber || item.isParent"
                  icon
                  @click="expand(!isExpanded)"
                >
                  <v-icon>{{ isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                </v-btn>
              </td>

              <td
                v-for="column in activeColumns"
                :key="column.value"
                :class="columnCellClass(column)"
                @click="column.value === 'balance' ? updateBalanceProduct(item) : null"
              >
                <template v-if="column.value === 'image'">
                  <v-avatar
                    size="44"
                    tile
                    class="product-thumb product-thumb-clickable"
                    @click.stop="handleProductImageClick(item)"
                  >
                    <v-progress-circular
                      v-if="imageLoadingIds[item.id]"
                      indeterminate
                      size="22"
                      width="2"
                      color="primary"
                    />
                    <v-img
                      v-else-if="productImageUrl(item)"
                      :src="productImageUrl(item)"
                      contain
                      @error="handleImageError(item)"
                    />
                    <v-icon v-else small color="grey">mdi-image-off-outline</v-icon>
                  </v-avatar>
                </template>

                <template v-else-if="column.value === 'localId'">
                  <v-tooltip bottom v-if="item.ChildProductId">
                    <template v-slot:activator="{ on }">
                      <v-icon small v-on="on" class="mr-1">mdi-arrow-right-bottom</v-icon>
                      <span v-html="$highlight(item.localId, search)" />
                    </template>
                    <span>Child Product</span>
                  </v-tooltip>
                  <span v-else v-html="$highlight(item.localId, search)" />
                </template>

                <template v-else-if="column.value === 'name'">
                  <div>
                    <span class="mr-2" v-html="$highlight(item.name, search)" />
                    <v-icon v-if="!item.remoteOrder" small color="orange">mdi-web-cancel</v-icon>
                  </div>
                  <div>
                    <span v-if="item.upc">UPC: <span class="caption" v-html="$highlight(item.upc, search)" /></span>
                    <span v-if="item.sku" class="ml-1">SKU: <span class="caption" v-html="$highlight(item.sku, search)" /></span>
                  </div>
                  <div v-if="item.Brand" class="caption">Brand: {{ item.Brand.name }}</div>
                  <div
                    v-if="item.note && currentBusiness.metadata && currentBusiness.metadata.productDescription"
                    class="caption"
                  >
                    {{ item.note }}
                  </div>
                </template>

                <template v-else-if="column.value === 'Department.name'">
                  <span
                    v-if="item.Department"
                    v-html="$highlight(item.Department.name, search)"
                  />
                  <span v-else>--</span>
                </template>

                <template v-else-if="column.value === 'balance'">
                  <v-chip
                    v-if="item.isParent && item.balance <= 0"
                    small
                    color="green lighten-4"
                    label
                    text-color="green darken-4"
                  >Parent</v-chip>
                  <template v-else-if="item.trackInventory">
                    <v-chip
                      v-if="item.balance <= 0"
                      x-small
                      color="#FDE8E4"
                      label
                      text-color="red"
                    >Out of Stock</v-chip>
                    <div>{{ item.balance }}</div>
                  </template>
                  <span v-else>--</span>
                </template>

                <template v-else-if="column.value === 'price'">
                  <span v-if="item.isParent">--</span>
                  <div v-else :class="{ 'red--text': item.dynamicPrice === item.price }">
                    {{ (item.dynamicPrice === item.price ? item.dynamicPrice : item.price) | currency }}
                    <span v-if="item.dynamicPrice === item.price"> *</span>
                    <v-icon v-if="currentUser.priceMarkupStatus" small class="ml-1">mdi-sale</v-icon>
                    <span v-if="currentBusiness.metadata && currentBusiness.metadata.boxCountOnInventory">
                      / {{ item.unitCountInBox }} = {{ item.unitPrice | currency }}
                    </span>
                  </div>
                </template>

                <template v-else-if="column.value === 'cost'">
                  <span v-if="item.isParent">--</span>
                  <span v-else-if="currentBusiness.metadata && currentBusiness.metadata.showAvgCostOnProduct">
                    {{ item.avgCost | currency }}
                  </span>
                  <span v-else>{{ item.cost | currency }}</span>
                </template>

                <template v-else-if="column.value === 'Category.name'">
                  <v-chip
                    :color="item.trackSerialNumber ? 'light-blue darken-1' : item.trackInventory ? 'teal darken-1' : 'yellow darken-4'"
                    text-color="white"
                  >
                    <v-icon small left>
                      {{ item.trackSerialNumber ? 'mdi-barcode-scan' : item.trackInventory ? 'mdi-package-variant-closed' : 'mdi-room-service-outline' }}
                    </v-icon>
                    {{ item.Category && item.Category.name ? item.Category.name : "--" }}
                  </v-chip>
                </template>
              </td>

              <td class="justify-end align-center text-right">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon><v-icon>more_vert</v-icon></v-btn>
                  </template>
                  <v-list dense class="menu-button">
                    <v-list-item v-if="checkRightStatus(17)" @click="editProduct(item)">
                      <v-list-item-title>{{ $t("buttons.edit") }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="checkRightStatus(9)" @click="deleteProduct(item)">
                      <v-list-item-title class="red--text">{{ $t("buttons.delete") }}</v-list-item-title>
                    </v-list-item>
                    <v-divider />
                    <v-list-item @click="item.trackSerialNumber ? inventoryAdj(item) : inventoryCount(item)">
                      <v-list-item-title>Inventory Count</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="!item.isParent" @click="openHistory(item)">
                      <v-list-item-title>Inventory History</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="openProductHistory(item)">
                      <v-list-item-title>View History</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
            <tr
              v-if="false"
              class="pointer"
              :class="{
                isParent: item.isParent,
                hightlightRed: costGreater(item),
              }"
            >
              <td v-if="bulkSelectionMode">
                <v-checkbox
                  :input-value="isSelected"
                  :disabled="!canBulkAdjust(item)"
                  hide-details
                  dense
                  class="ma-0 pa-0"
                  @change="select($event)"
                />
              </td>
              <td class="text-left product-name-cell">
                <v-avatar
                  v-if="!item.fix && !item.isParent"
                  size="5"
                  color="red"
                />
                <div v-if="item.trackSerialNumber">
                  <v-btn @click="expand(true)" icon v-if="!isExpanded">
                    <v-icon>mdi-chevron-down</v-icon>
                  </v-btn>
                  <v-btn @click="expand(false)" icon v-if="isExpanded">
                    <v-icon>mdi-chevron-up</v-icon>
                  </v-btn>
                </div>
                <div v-else-if="item.isParent">
                  <v-btn @click="expand(true)" icon v-if="!isExpanded">
                    <v-icon>mdi-chevron-down</v-icon>
                  </v-btn>
                  <v-btn @click="expand(false)" icon v-if="isExpanded">
                    <v-icon>mdi-chevron-up</v-icon>
                  </v-btn>
                </div>
              </td>
              <td v-if="showProductImages" class="text-center">
                <v-avatar
                  size="44"
                  tile
                  class="product-thumb product-thumb-clickable"
                  @click.stop="handleProductImageClick(item)"
                >
                  <v-progress-circular
                    v-if="imageLoadingIds[item.id]"
                    indeterminate
                    size="22"
                    width="2"
                    color="primary"
                  />
                  <v-img
                    v-else-if="productImageUrl(item)"
                    :src="productImageUrl(item)"
                    contain
                    @error="handleImageError(item)"
                  >
                    <template v-slot:placeholder>
                      <v-icon small color="grey">mdi-image-outline</v-icon>
                    </template>
                  </v-img>

                  <v-icon v-else small color="grey"
                    >mdi-image-off-outline</v-icon
                  >
                </v-avatar>
              </td>
              <td>
                <v-tooltip bottom v-if="item.ChildProductId">
                  <template v-slot:activator="{ on }">
                    <v-icon small v-on="on" class="mr-1"
                      >mdi-arrow-right-bottom</v-icon
                    >
                    <span v-html="$highlight(item.localId, search)">{{
                      item.localId
                    }}</span>
                  </template>
                  <span>Child Product </span>
                </v-tooltip>

                <span v-else v-html="$highlight(item.localId, search)">{{
                  item.localId
                }}</span>
              </td>
              <td class="text-left">
                <div>
                  <!-- <v-btn color="success" @click="updateBalanceProduct(item)">Fix Detail</v-btn> {{ item.fix }} -->
                  <div>
                    <span class="mr-2" v-html="$highlight(item.name, search)">
                      {{ item.name }}</span
                    >
                    <v-icon small v-if="!item.remoteOrder" color="orange"
                      >mdi mdi-web-cancel</v-icon
                    >
                  </div>
                  <div>
                    <span v-if="item.upc"
                      >UPC:
                      <span
                        class="caption"
                        v-html="$highlight(item.upc, search)"
                        >UPC: {{ item.upc }}</span
                      ></span
                    >

                    <span class="ml-1" v-if="item.sku"
                      >SKU:
                      <span
                        class="caption"
                        v-html="$highlight(item.sku, search)"
                        >SKU: {{ item.sku }}</span
                      ></span
                    >
                  </div>

                  <div v-if="item.Brand" class="caption">
                    Brand: {{ item.Brand.name }}
                  </div>
                  <div
                    v-if="
                      item.note &&
                      currentBusiness.metadata &&
                      currentBusiness.metadata.productDescription
                    "
                    class="caption"
                  >
                    {{ item.note }}
                  </div>
                </div>
              </td>
              <td class="text-left">
                <span
                  class="mr-2"
                  v-if="item.Department"
                  v-html="$highlight(item.Department.name, search)"
                  >{{ item.Department.name }}</span
                >
              </td>
              <td @click="updateBalanceProduct(item)" class="text-center">
                <div v-if="item.isParent">
                  <v-chip
                    v-if="item.balance <= 0"
                    small
                    color="green lighten-4"
                    label
                    text-color="green darken-4"
                  >
                    Parent
                  </v-chip>
                </div>
                <div v-else-if="item.trackInventory">
                  <div class="text-center">
                    <v-chip
                      v-if="item.balance <= 0"
                      x-small
                      color="#FDE8E4"
                      label
                      text-color="red"
                      >Out of Stock</v-chip
                    >
                  </div>
                  {{ item.balance }}
                </div>
                <div v-else>--</div>
              </td>

              <td class="text-right">
                <div v-if="item.dynamicPrice === item.price" class="red--text">
                  <div v-if="item.isParent">--</div>
                  <div v-else>{{ item.dynamicPrice | currency }} *</div>
                </div>
                <div v-else>
                  <div v-if="item.isParent">--</div>
                  <div v-else>
                    <span>{{ item.price | currency }}</span>
                    <v-icon
                      small
                      class="ml-1"
                      v-if="currentUser.priceMarkupStatus"
                      >mdi-sale</v-icon
                    >
                    <span
                      v-if="
                        currentBusiness &&
                        currentBusiness.metadata &&
                        currentBusiness.metadata.boxCountOnInventory
                      "
                      >/ {{ item.unitCountInBox }} =
                      {{ item.unitPrice | currency }}</span
                    >
                  </div>
                </div>
              </td>

              <td
                class="text-right"
                v-if="
                  currentBusiness.metadata &&
                  currentBusiness.metadata.showAvgCostOnProduct
                "
              >
                <div v-if="item.isParent">--</div>
                <div v-else>{{ item.avgCost | currency }}</div>
              </td>
              <td class="text-right" v-else>
                <div v-if="item.isParent">--</div>
                <div v-else>{{ item.cost | currency }}</div>
              </td>

              <td class="text-left">
                <v-chip
                  :color="
                    item.trackSerialNumber
                      ? 'light-blue darken-1'
                      : item.trackInventory
                      ? 'teal darken-1'
                      : 'yellow darken-4'
                  "
                  text-color="white"
                >
                  <v-avatar
                    left
                    class="light-blue darken-2"
                    v-if="item.trackSerialNumber"
                  >
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-icon small v-on="on">mdi-barcode-scan</v-icon>
                      </template>
                      <span>Track with IMEI number</span>
                    </v-tooltip>
                  </v-avatar>

                  <v-avatar
                    left
                    v-else-if="item.trackInventory"
                    class="teal darken-3"
                  >
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-icon small v-on="on"
                          >mdi-package-variant-closed</v-icon
                        >
                      </template>
                      <span>Track inventory on this product</span>
                    </v-tooltip>
                  </v-avatar>

                  <v-avatar left v-else class="yellow darken-3">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-icon small v-on="on"
                          >mdi-room-service-outline</v-icon
                        >
                      </template>
                      <span>Track inventory on this product</span>
                    </v-tooltip>
                  </v-avatar>

                  <!-- Safely render category name -->
                  <span
                    v-if="item.Category && item.Category.name"
                    v-html="$highlight(item.Category.name, search)"
                  >
                    {{ item.Category.name }}
                  </span>
                  <span v-else>--</span>
                </v-chip>
              </td>
              <td class="justify-end align-center text-right">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon>
                      <v-icon>more_vert</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense class="menu-button">
                    <v-list-item
                      v-if="checkRightStatus(17)"
                      @click="editProduct(item)"
                    >
                      <v-list-item-title>{{
                        $t("buttons.edit")
                      }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="checkRightStatus(9)"
                      @click="deleteProduct(item)"
                    >
                      <v-list-item-title class="red--text">{{
                        $t("buttons.delete")
                      }}</v-list-item-title>
                    </v-list-item>
                    <v-divider></v-divider>
                    <v-list-item
                      v-if="item.trackSerialNumber"
                      @click="inventoryAdj(item)"
                    >
                      <v-list-item-title>Inventory Count</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-else @click="inventoryCount(item)">
                      <v-list-item-title>Inventory Count</v-list-item-title>
                    </v-list-item>

                    <v-divider
                      v-if="item.trackSerialNumber && !item.isParent"
                    ></v-divider>
                    <v-list-item
                      v-if="!item.isParent"
                      @click="openHistory(item)"
                    >
                      <v-list-item-title>Inventory History</v-list-item-title>
                    </v-list-item>
                    <v-divider></v-divider>
                    <v-list-item @click="openProductHistory(item)">
                      <v-list-item-title>View History</v-list-item-title>
                    </v-list-item>

                    <!-- <RemoteOrderLink :globalLink="true" :productId="item.id" v-if="item.trackSerialNumber" /> -->
                    <!-- <v-list-item v-if="item.trackInventory && !item.isParent" @click="openSync(item)">
                      <v-list-item-title>Sync Balance</v-list-item-title>
                    </v-list-item> -->
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0">
              <v-card
                color="#F3F8FC"
                text
                class="elevation-0"
                v-if="item.trackSerialNumber"
              >
                <v-card-text class="title">
                  <PurchaseDetailList
                    :ProductId="item.id"
                    status="available"
                  ></PurchaseDetailList>
                </v-card-text>
              </v-card>
              <v-card v-else color="#F3F8FC" flat>
                <v-card-text class="title">
                  <PhoneList :ProductId="item.id"></PhoneList>
                </v-card-text>
              </v-card>
            </td>
          </template>
          <v-alert slot="no-results" :value="true" color="error" icon="warning"
            >Your search for "{{ search }}" found no results.</v-alert
          >
        </v-data-table>

        <v-pagination
          v-if="$vuetify.breakpoint.xsOnly && mobilePageCount > 1"
          v-model="page"
          :length="mobilePageCount"
          :total-visible="5"
          circle
          class="mobile-pagination mt-3"
        />
      </v-card-text>
    </v-card>

    <BulkInventoryAdjustDialog
      v-model="bulkAdjustDialog"
      :selectedProducts="selectedProducts"
      @done="bulkAdjustDone"
    />

    <InventoryAdjustDialog
      v-model="inventoryDialog"
      :product="selectedProduct"
      @done="onAdjustmentDone"
    />
    <v-dialog v-model="syncDialog" width="500">
      <v-card v-if="selectedProduct">
        <v-card-title>Sync Product</v-card-title>
        <v-container grid-list-lg>
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs12>
                <span>Name:</span>
                <span class="ml-2"
                  >{{ selectedProduct.id }} - {{ selectedProduct.name }}</span
                >
              </v-flex>
              <v-flex xs12>
                <span>Opening Balance:</span>
                <span class="ml-2">{{ selectedProduct.initBalance }}</span>
              </v-flex>
              <v-flex xs12>
                <span>Current Balance:</span>
                <span class="ml-2">{{ selectedProduct.balance }}</span>
              </v-flex>
              <v-flex xs12>
                <span>Total Purchase:</span>
                <span class="ml-2">{{ selectedProduct.totalPurchase }}</span>
              </v-flex>
              <v-flex xs12>
                <span>Restock:</span>
                <span class="ml-2">{{ selectedProduct.restock }}</span>
              </v-flex>

              <v-flex xs12>
                <span>Total Sold:</span>
                <span class="ml-2">{{ selectedProduct.totalSold }}</span>
              </v-flex>
              <v-flex xs12>
                <span>Mark Sold:</span>
                <span class="ml-2">{{ selectedProduct.markSold }}</span>
              </v-flex>

              <v-flex xs12>
                <span>Serial Balance:</span>
                <span class="ml-2">{{ selectedProduct.serialValue }}</span>
              </v-flex>

              <v-flex xs12>
                <span>Stock Adjustment:</span>
                <span class="ml-2">{{ selectedProduct.stockAdjustment }}</span>
                <!-- <v-btn small class="ml-1" color="success" @click="saveAdj">Save Adjustment</v-btn> -->
              </v-flex>

              <v-flex xs12>
                <span>Real Balance:</span>
                <span class="ml-2">{{ selectedProduct.realBalance }}</span>
              </v-flex>

              <v-flex xs12>
                <span>Adj Should be:</span>
                <span class="ml-2">{{ selectedProduct.shouldBeAdj }}</span>
              </v-flex>
              <v-flex
                xs12
                class="mt-5"
                v-if="selectedProduct.balance !== selectedProduct.realBalance"
              >
                <v-layout row wrap>
                  <!-- <v-flex xs6>
                    <v-btn color="success" @click="updateBalance">Update Balance</v-btn>
                  </v-flex> -->
                  <v-flex xs6 v-if="selectedProduct.shouldBeAdj !== 0">
                    <v-btn color="blue" @click="fixAdjustment"
                      >Fix Adjustment</v-btn
                    >
                  </v-flex>
                </v-layout>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-container>
      </v-card>
    </v-dialog>
    <v-dialog persistent v-model="historyDialog" width="500">
      <v-card>
        <ModelTitle title="Select Date" @close="closeHistoryDialog()" />

        <v-container grid-list-lg>
          <!-- <VueCtkDateTimePicker
            v-model="dateTime"
            inline
            range
            :onlyDate="true"
            formatted="MM/DD/YYYY"
          /> -->
          <DateTimePicker
            defaultRange="thisMonth"
            :range="true"
            :hideTime="true"
            v-model="dateTime"
          />
        </v-container>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="createReport()">
            Create Report
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="listOptionDialog" max-width="420">
      <v-card class="list-options-card">
        <div class="list-options-header">
          <div>
            <div class="list-options-title">List Options</div>
            <div class="list-options-subtitle">
              Customize how products appear and open.
            </div>
          </div>
          <v-btn icon small class="list-options-close" @click="listOptionDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="list-options-body">
          <div class="list-options-section-label">Actions</div>

          <v-btn
            block
            rounded
            outlined
            color="primary"
            class="list-option-action"
            @click="startBulkSelection"
          >
            <v-icon left small>mdi-counter</v-icon>
            Adjust Inventory
          </v-btn>

          <download-excel
            class="list-option-download"
            :fetch="fetchProductsForDownload"
            worksheet="Product List"
            :stringifyLongNum="true"
            type="xls"
            :fields="fields"
            name="product"
          >
            <v-btn
              block
              rounded
              outlined
              color="primary"
              class="list-option-action"
              :disabled="downloadedProductCount < 1"
              :loading="downloadLoading"
            >
              <v-icon left small>mdi-download</v-icon>
              Download Product List
            </v-btn>
          </download-excel>

          <div class="list-options-section-label mt-2">Display</div>

          <v-btn
            block
            rounded
            outlined
            color="primary"
            class="list-option-action mb-2"
            @click="openColumnEditor"
          >
            <v-icon left small>mdi-table-column</v-icon>
            Arrange &amp; Show Columns
          </v-btn>

          <div class="list-option-row">
            <div class="list-option-icon">
              <v-icon color="primary">mdi-open-in-new</v-icon>
            </div>
            <div class="list-option-copy">
              <div class="list-option-title">Open inventory in new tab</div>
              <div class="list-option-description">
                Keep the product list open while reviewing inventory.
              </div>
            </div>
            <v-switch v-model="openNewTabEnabled" inset dense hide-details />
          </div>

          <div class="list-option-row">
            <div class="list-option-icon">
              <v-icon color="primary">mdi-eye-off-outline</v-icon>
            </div>
            <div class="list-option-copy">
              <div class="list-option-title">Hide zero stock products</div>
              <div class="list-option-description">
                Show only products that currently have stock.
              </div>
            </div>
            <v-switch v-model="hideZeroProduct" inset dense hide-details />
          </div>

          <div class="list-option-row">
            <div class="list-option-icon">
              <v-icon color="primary">mdi-image-outline</v-icon>
            </div>
            <div class="list-option-copy">
              <div class="list-option-title">Show product images</div>
              <div class="list-option-description">
                Display product thumbnails in the list.
              </div>
            </div>
            <v-switch v-model="showProductImages" inset dense hide-details />
          </div>
        </div>

        <div class="list-options-footer">
          <v-btn
            block
            rounded
            depressed
            color="primary"
            @click="listOptionDialog = false"
          >
            Done
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="columnEditorDialog" max-width="520" persistent>
      <v-card class="column-editor-card">
        <div class="list-options-header">
          <div>
            <div class="list-options-title">Product List Columns</div>
            <div class="list-options-subtitle">
              Drag columns into position and hide anything customers should not see.
            </div>
          </div>
          <v-btn icon small @click="columnEditorDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="column-editor-body">
          <draggable
            v-model="draftColumnSettings"
            handle=".column-drag-handle"
            ghost-class="column-drag-ghost"
          >
            <div
              v-for="column in draftColumnSettings"
              :key="column.value"
              class="column-editor-row"
            >
              <v-icon class="column-drag-handle">mdi-drag</v-icon>
              <div class="column-editor-name">{{ column.text }}</div>
              <v-switch
                v-model="column.visible"
                dense
                inset
                hide-details
                :aria-label="`Show ${column.text} column`"
              />
            </div>
          </draggable>
        </div>

        <div class="column-editor-footer">
          <v-btn text rounded @click="resetColumnLayout">Reset Default</v-btn>
          <v-spacer />
          <v-btn text rounded @click="columnEditorDialog = false">Cancel</v-btn>
          <v-btn
            rounded
            depressed
            color="primary"
            :loading="savingColumnLayout"
            @click="saveColumnLayout"
          >
            Save for Business
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
    <ProductHistoryDialog
      v-show="productHistoryDialog"
      v-model="productHistoryDialog"
      :product="historyProduct"
    />

    <!-- Mobile Bottom Sheet Filter Drawer -->
    <v-bottom-sheet v-model="mobileFilterDrawer" inset max-width="520">
      <v-card class="mobile-filter-card">
        <div class="mobile-filter-header">
          <v-btn icon @click="mobileFilterDrawer = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <div class="mobile-filter-title">Filters</div>
        </div>

        <v-card-text>
          <v-btn
            block
            rounded
            outlined
            color="primary"
            class="mb-4"
            @click="openMobileListOptions"
          >
            <v-icon left small>mdi-tune</v-icon>
            List Options
          </v-btn>

          <v-autocomplete
            v-model="selectedDepartment"
            :items="departmentList"
            return-object
            item-text="name"
            clearable
            outlined
            dense
            hide-details
            label="Department"
            @change="getList()"
          />

          <v-autocomplete
            v-model="selectedBrand"
            :items="brandList"
            return-object
            item-text="name"
            clearable
            outlined
            dense
            hide-details
            label="Brand"
            class="mt-3"
            @change="getList()"
          />

          <v-autocomplete
            v-model="selectedCategory"
            :items="categoryList"
            return-object
            item-text="name"
            clearable
            outlined
            dense
            hide-details
            label="Category"
            class="mt-3"
            @change="getList()"
          />

          <v-select
            v-model="itemsPerPage"
            :items="[10, 20, 50, 100]"
            label="Rows per page"
            outlined
            dense
            hide-details
            class="mt-3"
          />

          <v-btn
            block
            rounded
            depressed
            color="primary"
            class="mt-4"
            @click="mobileFilterDrawer = false"
          >
            Apply
          </v-btn>
        </v-card-text>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
import productService from "../service";
import PurchaseDetailList from "@/modules/Inventory/Purchase/components/PurchaseDetailList";
import purchaseService from "@/modules/Inventory/Purchase/service.js";
import { mapGetters } from "vuex";
import GlobalFunction from "@/mixins/GlobalFunction";
import PhoneList from "@/modules/Product/components/PhoneList";
import departmentService from "@/modules/Product/Department/service.js";
import brandService from "@/modules/Product/Brand/service.js";
import categroyService from "@/modules/Product/Category/service.js";
import BulkInventoryAdjustDialog from "@/modules/Product/components/BulkInventoryAdjustDialog.vue";
import _ from "lodash";
import InventoryAdjustDialog from "@/modules/Product/components/InventoryAdjustDialog";
import ProductHistoryDialog from "@/modules/Product/components/ProductHistory.vue";
import ProductImagePreviewDialog from "@/components/Image/ProductImagePreviewDialog.vue";
import draggable from "vuedraggable";
import settingService from "@/modules/Setting/service";

const DateTimePicker = () => import("@/components/DateTimePicker");

const Create = () => import("./Create");
const Edit = () => import("./Edit");
const PRODUCT_LIST_OPTIONS_KEY = "productListOptions";
const DEFAULT_PRODUCT_COLUMNS = [
  { value: "image", text: "Image", visible: false },
  { value: "localId", text: "ID", visible: true },
  { value: "name", text: "Name", visible: true },
  { value: "Department.name", text: "Department", visible: true },
  { value: "balance", text: "Stock", visible: true },
  { value: "price", text: "Price", visible: true },
  { value: "cost", text: "Cost", visible: true },
  { value: "Category.name", text: "Category", visible: true },
];
// const RemoteOrderLink = () => import("@/components/RemoteOrderLink");

export default {
  name: "product-list",
  data() {
    return {
      listOptionDialog: false,
      columnEditorDialog: false,
      savingColumnLayout: false,
      columnSettings: DEFAULT_PRODUCT_COLUMNS.map((column) => ({ ...column })),
      draftColumnSettings: [],
      imagePreviewDialog: false,
      imagePreviewProduct: null,
      imageLoadingIds: {},
      bulkAdjustDialog: false,
      bulkSelectionMode: false,
      selectedProducts: [],
      mobileFilterDrawer: false,
      productHistoryDialog: false,
      historyProduct: null,
      departmentList: [],
      selectedDepartment: null,
      brandList: [],
      stockFilter: [],
      categoryList: [],
      selectedCategory: null,
      selectedBrand: null,
      toggle_exclusive: 2,
      toggle_multiple: [1, 2, 3], // temp
      historyDialog: false,
      dateTime: {
        start: this.$moment().startOf("month").toISOString(),
        end: this.$moment().endOf("month").toISOString(),
      },
      openNewTab: [],
      id: 0,
      expanded: [],
      singleExpand: true,
      downloadLoading: false,
      hideZeroProduct: false,
      createDialog: false,
      editDialog: false,
      downloadedProducts: [],
      selectedProduct: null,
      inventoryDialog: false,
      syncDialog: false,
      fields: {
        ID: "localId",
        UPC: "upc",
        SKU: "sku",
        ImageUrl: "imageUrl",
        Name: "name",
        Category: "Category.name",
        Department: "Department.name",
        Brand: "Brand.name",
        Stock: "balance",
        unitCount: "unitCountInBox",
        Price: "price",
        AvgCost: "avgCost",
        Cost: "cost",
      },
      headers: [
        { text: "", value: "data-table-expand" },
        {
          text: "ID",
          value: "localId",
          sortable: true,
        },
        {
          text: this.$t("labels.name"),
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: "Department",
          align: "left",
          value: "Department.name",
          sortable: true,
        },

        {
          text: this.$t("labels.stock"),
          value: "balance",
          align: "center",
          sortable: true,
        },
      ],
      products: [],
      totalItems: 0,
      search: "",
      loading: true,
      page: 1,
      sortBy: "balance",
      sortDesc: true,
      downloadedProductCount: 5000,
      pageCount: 0,
      itemsPerPage: 10,
    };
  },
  created() {
    this.headers.push({
      text: "Price",
      value: "price",
      align: "right",
      sortable: true,
    });

    if (
      this.currentBusiness.metadata &&
      this.currentBusiness.metadata.showAvgCostOnProduct
    ) {
      this.headers.push({
        text: "Avg Cost",
        value: "avgCost",
        align: "center",
        sortable: true,
      });
    } else {
      this.headers.push({
        text: "Cost",
        value: "cost",
        align: "center",
        sortable: true,
      });
    }

    this.headers.push(
      {
        text: "Category",
        value: "Category.name",
        align: "left",
        sortable: false,
      },
      {
        text: "",
        value: "action",
        sortable: false,
      }
    );
    this.getDepartment();
    this.getBrand();
    this.getCategory();
    this.loadListOptions();
    this.getList();
    if (this.$route.query && this.$route.query.search) {
      this.search = this.$route.query.search;
    }
    document.title = "smplsale - Product";
  },
  components: {
    draggable,
    Create,
    DateTimePicker,
    ProductHistoryDialog,
    ProductImagePreviewDialog,
    InventoryAdjustDialog,
    Edit,
    PurchaseDetailList,
    PhoneList,
    BulkInventoryAdjustDialog,
  },
  mixins: [GlobalFunction],
  watch: {
    page() {
      this.getList();
    },
    itemsPerPage() {
      this.page = 1; // reset to first page when size changes
      this.getList();
    },
    sortBy() {
      this.page = 1;
      this.getList();
    },
    sortDesc() {
      this.page = 1;
      this.getList();
    },
    // stockFilter() {
    //   this.hideZeroProduct = !this.hideZeroProduct;
    // },
    hideZeroProduct() {
      this.getList();
    },
    search: _.debounce(function (val) {
      const search = val || "";

      if (search.length > 0 && search.length <= 2) return;

      // Server pagination must restart for a new result set. Changing `page`
      // triggers the page watcher, so do not call getList twice here.
      if (this.page !== 1) {
        this.page = 1;
        return;
      }

      this.getList();
    }, 300),
  },
  computed: {
    ...mapGetters("global", [
      "currentUser",
      "checkRightStatus",
      "currentBusiness",
    ]),
    openNewTabEnabled: {
      get() {
        return this.openNewTab.length > 0;
      },
      set(value) {
        this.openNewTab = value ? [1] : [];
      },
    },
    showProductImages: {
      get() {
        const imageColumn = this.columnSettings.find(
          (column) => column.value === "image"
        );
        return !!(imageColumn && imageColumn.visible);
      },
      set(value) {
        const imageColumn = this.columnSettings.find(
          (column) => column.value === "image"
        );
        if (imageColumn) imageColumn.visible = value;
        this.saveListOptions();
      },
    },
    visibleHeaders() {
      return [
        { text: "", value: "data-table-expand", sortable: false },
        ...this.activeColumns.map((column) => this.headerForColumn(column)),
        { text: "", value: "action", sortable: false },
      ];
    },
    activeColumns() {
      return this.columnSettings.filter((column) => column.visible);
    },
    mobilePageCount() {
      return Math.max(1, Math.ceil(this.totalItems / this.itemsPerPage));
    },
  },
  methods: {
    productRowClass(item) {
      return {
        isParent: item.isParent,
        hightlightRed: this.costGreater(item),
      };
    },
    columnCellClass(column) {
      if (["price", "cost"].includes(column.value)) return "text-right";
      if (["image", "balance"].includes(column.value)) return "text-center";
      return "text-left";
    },
    headerForColumn(column) {
      const align = ["price", "cost"].includes(column.value)
        ? "right"
        : column.value === "balance" || column.value === "image"
        ? "center"
        : "left";
      return {
        text:
          column.value === "cost" &&
          this.currentBusiness.metadata &&
          this.currentBusiness.metadata.showAvgCostOnProduct
            ? "Avg Cost"
            : column.text,
        value:
          column.value === "cost" &&
          this.currentBusiness.metadata &&
          this.currentBusiness.metadata.showAvgCostOnProduct
            ? "avgCost"
            : column.value,
        align,
        sortable: !["image", "Category.name"].includes(column.value),
      };
    },
    normalizeColumnSettings(savedColumns) {
      const saved = Array.isArray(savedColumns) ? savedColumns : [];
      const byValue = new Map(saved.map((column) => [column.value, column]));
      const ordered = saved
        .map((column) => DEFAULT_PRODUCT_COLUMNS.find((item) => item.value === column.value))
        .filter(Boolean)
        .map((column) => ({
          ...column,
          visible: byValue.get(column.value).visible !== false,
        }));
      DEFAULT_PRODUCT_COLUMNS.forEach((column) => {
        if (!ordered.some((item) => item.value === column.value)) {
          ordered.push({ ...column });
        }
      });
      return ordered;
    },
    openColumnEditor() {
      this.draftColumnSettings = this.columnSettings.map((column) => ({
        ...column,
      }));
      this.columnEditorDialog = true;
      this.listOptionDialog = false;
    },
    resetColumnLayout() {
      this.draftColumnSettings = DEFAULT_PRODUCT_COLUMNS.map((column) => ({
        ...column,
      }));
    },
    async saveColumnLayout() {
      if (!this.draftColumnSettings.some((column) => column.visible)) {
        this.$swal.fire({ icon: "warning", title: "Keep at least one column visible." });
        return;
      }

      this.savingColumnLayout = true;
      try {
        const columns = this.normalizeColumnSettings(this.draftColumnSettings);
        const business = _.cloneDeep(this.currentBusiness);
        business.metadata = business.metadata || {};
        business.metadata.productListColumns = columns.map(({ value, visible }) => ({
          value,
          visible,
        }));

        const result = await settingService.update(business.id, business);
        if (!result.status) throw new Error(result.message || "Unable to save column layout.");

        this.columnSettings = columns;
        this.$store.dispatch("global/initBusiness", business);
        this.saveListOptions();
        this.columnEditorDialog = false;
        this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 2500 }).fire({
          icon: "success",
          title: "Product columns saved for this business.",
        });
      } catch (error) {
        this.$swal.fire({
          icon: "error",
          title: "Column layout was not saved",
          text: (error && error.message) || "Please try again.",
        });
      } finally {
        this.savingColumnLayout = false;
      }
    },
    startBulkSelection() {
      this.selectedProducts = [];
      this.bulkSelectionMode = true;
      this.mobileFilterDrawer = false;
      this.listOptionDialog = false;
    },
    openMobileListOptions() {
      this.mobileFilterDrawer = false;
      this.listOptionDialog = true;
    },
    cancelBulkSelection() {
      this.selectedProducts = [];
      this.bulkSelectionMode = false;
    },
    loadListOptions() {
      try {
        const businessColumns =
          this.currentBusiness.metadata &&
          this.currentBusiness.metadata.productListColumns;
        if (Array.isArray(businessColumns)) {
          this.columnSettings = this.normalizeColumnSettings(businessColumns);
          return;
        }

        const saved = localStorage.getItem(PRODUCT_LIST_OPTIONS_KEY);
        if (!saved) return;

        const options = JSON.parse(saved);

        if (Array.isArray(options.columnSettings)) {
          this.columnSettings = this.normalizeColumnSettings(options.columnSettings);
        } else if (options.showProductImages) {
          const imageColumn = this.columnSettings.find(
            (column) => column.value === "image"
          );
          if (imageColumn) imageColumn.visible = true;
        }
      } catch (e) {
        console.error("Failed to load product list options", e);
      }
    },

    saveListOptions() {
      try {
        const options = {
          showProductImages: this.showProductImages,
          columnSettings: this.columnSettings,
        };

        localStorage.setItem(PRODUCT_LIST_OPTIONS_KEY, JSON.stringify(options));
      } catch (e) {
        console.error("Failed to save product list options", e);
      }
    },
    productThumbUrl(item) {
      return (
        item.thumbnailUrl ||
        item.thumbnailURL ||
        item.thumbnail ||
        item.thumbUrl ||
        item.thumb ||
        null
      );
    },

    productFullImageUrl(item) {
      return item.imageUrl || item.imageURL || item.image || null;
    },

    productImageUrl(item) {
      if (!item || item._imageFailed) return null;

      const rawThumb = this.productThumbUrl(item);
      const rawFull = this.productFullImageUrl(item);
      const thumb = this.shouldHideWebImage(item, rawThumb) ? null : rawThumb;
      const full = this.shouldHideWebImage(item, rawFull) ? null : rawFull;

      if (item._imageUseFull) return full || thumb;

      return thumb || full;
    },
    shouldHideWebImage(item, url) {
      const settings =
        (this.currentBusiness && this.currentBusiness.metadata) || {};
      if (settings.generateAiProductImage !== false) return false;

      const metadata = (item && item.metadata) || {};
      const source = metadata.imageSource;
      if (source === "web-search" || source === "auto-search") return true;
      if (source === "upload" || source === "url") return false;

      return metadata.autoImageDisabled === false &&
        /\/v1\/assets\/asset_[a-f0-9]{24}(?:\/thumbnail)?(?:\?|$)/i.test(
          String(url || "")
        );
    },

    handleImageError(item) {
      const thumb = this.productThumbUrl(item);
      const full = this.productFullImageUrl(item);

      if (!item._imageUseFull && full && full !== thumb) {
        this.$set(item, "_imageUseFull", true);
        return;
      }

      this.$set(item, "_imageFailed", true);
    },
    async handleProductImageClick(item) {
      if (!item) return;

      const visibleImage = this.productImageUrl(item);
      if (visibleImage) {
        this.openImagePreview(item);
        return;
      }

      const settings =
        (this.currentBusiness && this.currentBusiness.metadata) || {};
      if (settings.generateAiProductImage === false) return;

      if (this.imageLoadingIds[item.id]) return;
      this.$set(this.imageLoadingIds, item.id, true);
      try {
        const searchResponse = await productService.searchProductImages({
          q: item.name,
          brand: item.Brand && item.Brand.name,
          upc: item.upc,
          sku: item.sku,
        });
        const searchResult = searchResponse && searchResponse.data
          ? searchResponse.data
          : searchResponse;
        const candidate = searchResult && searchResult.candidates
          ? searchResult.candidates[0]
          : null;
        if (!candidate) throw new Error("No image found");

        const importResponse = await productService.importProductImage({
          identity: {
            name: item.name,
            brand: item.Brand && item.Brand.name,
            upc: item.upc,
            sku: item.sku,
          },
          sourceUrl: candidate.url,
          sourcePage: candidate.sourcePage || null,
          visibility: "shared_pending",
        });
        const imported = importResponse && importResponse.data
          ? importResponse.data
          : importResponse;
        if (!imported || !imported.imageUrl) throw new Error("Image import failed");

        const metadata = {
          ...(item.metadata || {}),
          autoImageDisabled: false,
          imageSource: "web-search",
        };
        await productService.update(item.id, {
          imageUrl: imported.imageUrl,
          thumbnailUrl: imported.thumbnailUrl,
          metadata,
        });

        this.$set(item, "imageUrl", imported.imageUrl);
        this.$set(item, "thumbnailUrl", imported.thumbnailUrl);
        this.$set(item, "metadata", metadata);
        this.$set(item, "_imageFailed", false);
        this.$set(item, "_imageUseFull", false);
        this.openImagePreview(item);
      } catch (error) {
        this.$swal({
          icon: "info",
          title: "No image found",
          text: "Try editing the product and searching with a more specific name.",
        });
      } finally {
        this.$delete(this.imageLoadingIds, item.id);
      }
    },
    async repairPreviewImage(item) {
      if (!item) return;
      this.imagePreviewDialog = false;
      this.$set(item, "_imageFailed", true);
      await this.handleProductImageClick(item);
    },
    openImagePreview(item) {
      if (!this.productFullImageUrl(item) && !this.productThumbUrl(item)) return;

      this.imagePreviewProduct = item;
      this.imagePreviewDialog = true;
    },
    canBulkAdjust(item) {
      if (!item) return false;
      if (item.isParent) return false;
      if (!item.trackInventory) return false;
      if (item.trackSerialNumber) return false;

      return true;
    },
    async bulkAdjustDone(updated) {
      if (!updated) return;

      this.selectedProducts = [];
      this.bulkSelectionMode = false;
      await this.getList();
    },
    openProductHistory(product) {
      if (!this.currentBusiness.metadata.historyTracking) {
        this.$swal({
          icon: "warning",
          title: "History Tracking Off",
          text: "Enable it in Settings to view change logs ($50/month).",
        });
        return;
      }
      this.historyProduct = product;
      this.productHistoryDialog = true;
    },
    async onAdjustmentDone(updated) {
      if (updated) {
        this.updateBalanceProduct(this.selectedProduct);
        await this.getList(); // reload list
      }
    },
    getDepartment() {
      return departmentService.getAll().then((response) => {
        // console.log('response', response)
        // this.loading = false;
        this.departmentList = response.data;
        return response;
      });
    },
    getBrand() {
      return brandService.getAll().then((response) => {
        // console.log('response', response)
        // this.loading = false;
        this.brandList = response.data;
        return response;
      });
    },
    getCategory() {
      return categroyService.getAll().then((response) => {
        // console.log('response', response)
        // this.loading = false;
        this.categoryList = response.data;
        return response;
      });
    },

    updateBalanceProduct(product) {
      // console.log('i call this')
      productService
        .updateBalance(product.id)
        .then((result) => {
          console.log("update balance result", result);
          this.getList();
        })
        .catch((err) => {
          console.log(err);
        });
    },
    fix() {
      // let newBoxPrice = parseFloat(row.unitPrice) * parseFloat(row.unitCountInBox)
      // console.log('before', parseFloat(row.boxPrice), parseFloat(row.unitPrice), parseFloat(row.price), parseFloat(row.boxPrice) > parseFloat(row.price))

      return productService
        .fix()
        .then((result) => {
          if (result.status) {
            console.log("result", result);
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "success",
                title: "Product is updated",
              });
            // this.getList();
          }
        })
        .catch((err) => {
          this.loading = false;
          // console.log("err", err);
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: err.data.message,
            });
        });

      // if (!row.isParent && row.unitCountInBox > 0) {
      //   if (!row.fix) {
      //     console.log('in iter')
      //     let newCost = 0
      //     let newPrice = parseFloat(row.price) * parseFloat(row.unitCountInBox)
      //     row.price = newPrice
      //     row.fix = true
      //     if (row.cost) {
      //       newCost = parseFloat(row.cost) * parseFloat(row.unitCountInBox)

      //       row.cost = newCost
      //     }

      //     console.log('before', newPrice, newCost, row.fix)

      //   }
      // }
    },
    async fetchProductsForDownload() {
      try {
        this.downloadLoading = true;

        const where = {
          search: "", // full export ignores current search by your original behavior—keep if desired
          hideZero: this.hideZeroProduct,
          limit: this.downloadedProductCount, // 5000
          offset: 0,
        };
        if (this.selectedCategory) where.CategoryId = this.selectedCategory.id;
        if (this.selectedBrand) where.BrandId = this.selectedBrand.id;
        if (this.selectedDepartment)
          where.DepartmentId = this.selectedDepartment.id;

        const response = await productService.getAll(where);
        const res = response.data;
        const rows = Array.isArray(res) ? res : res.rows || res.items || [];
        this.downloadedProducts = rows;
        return rows; // download-excel uses this
      } finally {
        this.downloadLoading = false;
      }
    },
    costGreater(item) {
      if (item.isParent) return false;
      if (
        this.currentBusiness.metadata &&
        this.currentBusiness.metadata.showAvgCostOnProduct
      ) {
        if (item.avgCost > item.price) return true;
        return false;
      }
      if (item.cost > item.price) return true;
      return false;
    },
    fixAdjustment() {
      let obj = {
        adjustmentQuantity: this.selectedProduct.shouldBeAdj,
        reason: "Manual Adjustment",
        adjustmentType: "Decrease",
        ProductId: this.selectedProduct.id,
      };
      if (this.selectedProduct.shouldBeAdj > 0) {
        obj.adjustmentType = "Increase";
      }

      console.log("obj", obj);

      return productService.stockAdjustment
        .create(obj)
        .then((result) => {
          if (result.status) {
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "success",
                title: "Product is updated",
              });
            // this.empty();

            this.getList();
            this.updateStock(this.selectedProduct);
            this.loading = false;
            this.syncDialog = false;
            // this.$emit("submit");
          }
        })
        .catch((err) => {
          this.loading = false;
          console.log("err", err);
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: err.data.message,
            });
        });
    },
    updateBalance() {
      // let initBalance = this.selectedProduct.realBalance + this.selectedProduct
      return productService
        .update(this.selectedProduct.id, {
          initBalance: this.selectedProduct.realBalance,
        })
        .then((result) => {
          if (result.status) {
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "success",
                title: "Product is updated",
              });
            // this.empty();

            this.getList();
            this.loading = false;
            this.syncDialog = false;
            // this.$emit("submit");
          }
        })
        .catch((err) => {
          this.loading = false;
          // console.log("err", err);
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: err.data.message,
            });
        });
    },
    openHistory(item) {
      // const routeData = this.$router.resolve({
      //   path: `/global/statement/${hashId}/view?start=${this.dateTime.start}&end=${this.dateTime.end}`,
      // });
      // window.open(routeData.href, "newwindow", "width=850, height=1100");
      // this.dialog = false;
      this.historyDialog = true;
      this.selectedProduct = item;
      // let routeData = this.$router.resolve({
      //   path: `/reportView/product/history/${item.id}`,
      // });
      // window.open(routeData.href, "_blank");
    },
    closeHistoryDialog() {
      // console.log('call close')
      this.historyDialog = false;
    },
    createReport() {
      this.closeHistoryDialog();
      let routeData = this.$router.resolve({
        path: `/reportView/product/history/${this.selectedProduct.id}?start=${this.dateTime.start}&end=${this.dateTime.end}`,
      });
      window.open(routeData.href, "_blank");
    },
    openSync(item) {
      productService.getSyncInfo(item.id).then((response) => {
        console.log("response", response);
        this.selectedProduct = response.data;
        // this.selectedProduct.shouldBeAdj = parseFloat(this.selectedProduct.balance) - parseFloat(this.selectedProduct.realBalance)
        // item.avgCost = response.data.avgCost;
        // this.selectedProduct.totalPurchase = response.data.totalPurchase;
        // this.selectedProduct.totalSold = response.data.totalSold;
      });
      this.syncDialog = !this.syncDialog;
    },
    updateAllList() {
      let index = 0;
      this.products.map((row) => {
        if (row.trackSerialNumber) {
          index += this.updateStock(row);
          console.log("index", index);
        }
      });
    },
    updateStock(row) {
      if (row.trackSerialNumber) {
        const where = {
          status: "available",
          ProductId: row.id,
        };
        return purchaseService.getSerialVault(where).then(async (response) => {
          const stock = response.data;
          console.log("stock", stock.length);
          row.balance = stock.length;
          // this.Product.balance = this.stocks.length;
          // console.log("product ", this.Product);
          productService.update(row.id, row).then((response) => {
            console.log("response", response);
          });
        });
      } else {
        console.log("stock row", row);
      }
    },
    inventoryAdj(item) {
      if (this.openNewTab.length !== 0) {
        let routeData = this.$router.resolve({
          path: `/product/inventoryAdj/${item.id}`,
        });
        window.open(routeData.href, "_blank");
      } else {
        this.$router.push({ path: `/product/inventoryAdj/${item.id}` });
      }
      // this.$router.push({ path: `/product/inventoryAdj/${item.id}` });
    },
    inventoryCount(item) {
      this.selectedProduct = item;
      this.updateBalanceProduct(this.selectedProduct);
      this.inventoryDialog = !this.inventoryDialog;
    },
    add() {
      this.createDialog = !this.createDialog;
    },
    getList() {
      this.loading = true;
      this.editDialog = false;
      this.createDialog = false;

      // Normalize Vuetify sort (Vuetify can pass arrays)
      const sb = Array.isArray(this.sortBy) ? this.sortBy[0] : this.sortBy;
      const sd = Array.isArray(this.sortDesc)
        ? this.sortDesc[0]
        : this.sortDesc;
      const sortBy = sb || "id";
      const sortDesc = !!sd;

      const where = {
        search: this.search,
        hideZero: this.hideZeroProduct,
        limit: this.itemsPerPage,
        offset: (this.page - 1) * this.itemsPerPage,

        // For backend to consume
        sortBy,
        sortDesc,

        // Backward compatibility if your backend still uses orderBy
        orderBy: [sortBy, String(sortDesc)],
      };

      if (this.selectedCategory) where.CategoryId = this.selectedCategory.id;
      if (this.selectedBrand) where.BrandId = this.selectedBrand.id;
      if (this.selectedDepartment)
        where.DepartmentId = this.selectedDepartment.id;

      return productService
        .getAll(where)
        .then((response) => {
          this.loading = false;
          // console.log('response', response)
          // YOUR API SHAPE: { code, status, context, httpStatus, count, data }
          const res = response; // <-- no .data
          const rows = res.data || []; // paged rows
          const cnt = Number(res.count) || 0;

          this.totalItems = cnt;

          this.products = rows.map((row) => {
            if (
              this.currentBusiness.metadata &&
              this.currentBusiness.metadata.dynamicPrice
            ) {
              row.dynamicPrice = this.getDynamicPrice(
                row,
                this.currentBusiness.metadata.dynamicPriceList
              );
            }
            if (this.currentUser.priceMarkupStatus) {
              const base = parseFloat(row.price) || 0;
              const pct = parseFloat(this.currentUser.priceMarkup) || 0;
              row.price = parseFloat((base * (1 + pct / 100)).toFixed(2));
            }
            return row;
          });

          return response;
        })
        .catch((err) => {
          this.loading = false;
          console.error(err);
        });
    },
    editProduct(product) {
      this.selectedProduct = product;
      this.id = product.id;
      this.editDialog = !this.editDialog;
    },
    deleteProduct(product) {
      console.log("delete product", product);
      this.updateBalanceProduct(product);

      // if (product.balance > 0) {
      //   this.$swal({
      //     text: "Delete",
      //     icon: "info",
      //     html: `<b>${product.name}</b> have <b style="color: red;">${product.balance}</b>  items in stock remove them before you delete the product.`,
      //   });
      //   return false;
      // }

      if (
        this.currentBusiness.metadata &&
        this.currentBusiness.metadata.deletePrecaution
      ) {
        this.$swal
          .fire({
            title: "Are you sure?",
            html: `You won't be able to revert this.</br> <div style="color:red;">Type <span style="font-weight: bold">delete</span> in order to remove this</div>`,
            icon: "question",
            input: "text",
            inputAttributes: {
              autocapitalize: "off",
            },
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Delete",
          })
          .then((result) => {
            if (result.value && result.value === "delete") {
              this.loadingDialog = true;
              productService
                .delete(product.id)
                .then((response) => {
                  // console.log("response delete", response);
                  this.loadingDialog = false;
                  if (response.data) {
                    this.$swal
                      .mixin({
                        toast: true,
                        position: "top-end",
                        showConfirmButton: false,
                        timer: 3000,
                      })
                      .fire({
                        icon: "success",
                        title: "Product has been deleted.",
                      });
                    // this.$swal("Deleted!", "Invoice has been deleted.", "success");
                    this.getList();
                    this.$events.emit("customerProfileEvent");
                    this.$events.emit("CustomerMiniList");
                  }
                })
                .catch((error) => {
                  this.loadingDialog = false;
                  this.$swal({
                    title: "Delete Invoice",
                    text: error.data.message,
                    type: "error",
                  });
                });
            } else if (result.dismiss !== "cancel") {
              this.$swal("Delete", 'Type "delete" to delete item', "error");
            }
          });
      } else {
        this.$swal({
          title: "Are you sure?",
          html: `<b>${product.name}</b> have <b style="color: red;">${product.balance}</b>  items in stock remove them before you delete the product.`,
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        }).then((result) => {
          if (result.value) {
            productService.delete(product.id).then((response) => {
              if (response.data) {
                this.$swal("Deleted!", "Product has been deleted.", "success");
                this.getList();
              }
            });
          }
        });
      }
    },
  },
};
</script>
<style scoped>
.product-list-page {
  width: 100%;
}

.product-card {
  border-radius: 20px;
  background: #ffffff;
}

.product-header {
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  border-bottom: 1px solid #e2e8f0;
}

.product-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

.product-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.product-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bulk-selection-toolbar {
  min-height: 58px;
  padding: 10px 12px;
  border: 1px solid #bfdbfe;
  border-radius: 14px;
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.bulk-selection-title {
  color: #0f172a;
  font-weight: 800;
}

.bulk-selection-count {
  color: #64748b;
  font-size: 12px;
}

.bulk-selection-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.product-filter-bar {
  padding: 12px 16px;
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: nowrap;
  border-bottom: 1px solid #e2e8f0;
}

.search-field {
  flex: 1 1 auto;
  min-width: 260px;
}

.product-search {
  width: 100%;
  max-width: none;
}

.filter-input {
  width: 210px;
  max-width: 210px;
}

.options-field {
  flex: 0 0 auto;
}

.options-btn {
  height: 40px !important;
  min-width: 130px;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filter-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.quick-options {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 1px;
}

.active-option {
  color: #ffffff !important;
}

.isParent {
  background: #e8f5e9;
}

.hightlightRed {
  background: #ffcdd2;
}

.pointer:hover {
  background: #f8fafc;
}

.product-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.menu-button::v-deep .v-list-item__title {
  font-weight: 600;
}

.menu-button {
  border-radius: 12px;
  overflow: hidden;
}

.product-thumb {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.product-thumb-clickable {
  cursor: zoom-in;
  transition: border-color 0.2s ease, box-shadow 0.2s ease,
    transform 0.2s ease;
}

.product-thumb-clickable:hover {
  border-color: var(--v-primary-base);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.14);
  transform: translateY(-1px);
}

.list-options-card {
  border-radius: 20px;
  overflow: hidden;
}

.list-options-header {
  padding: 20px 20px 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.list-options-title {
  color: #0f172a;
  font-size: 20px;
  font-weight: 800;
}

.list-options-subtitle {
  color: #64748b;
  font-size: 13px;
  margin-top: 3px;
}

.list-options-close {
  background: #f8fafc;
}

.list-options-body {
  padding: 14px;
  display: grid;
  gap: 10px;
}

.list-options-section-label {
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.list-option-action {
  min-height: 42px;
}

.list-option-download {
  display: block;
}

.list-option-row {
  min-height: 76px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #ffffff;
  display: flex;
  align-items: center;
  gap: 12px;
}

.list-option-icon {
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  border-radius: 12px;
  background: #fff8db;
  display: flex;
  align-items: center;
  justify-content: center;
}

.list-option-copy {
  flex: 1;
  min-width: 0;
}

.list-option-title {
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
}

.list-option-description {
  color: #64748b;
  font-size: 12px;
  line-height: 1.35;
  margin-top: 2px;
}

.list-options-footer {
  padding: 4px 14px 16px;
}

.column-editor-card {
  overflow: hidden;
  border-radius: 20px !important;
}

.column-editor-body {
  max-height: 480px;
  overflow-y: auto;
  padding: 14px;
  background: #f8fafc;
}

.column-editor-row {
  min-height: 54px;
  margin-bottom: 8px;
  padding: 7px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 13px;
  background: #ffffff;
  display: flex;
  align-items: center;
  gap: 10px;
}

.column-editor-row:last-child {
  margin-bottom: 0;
}

.column-drag-handle {
  cursor: grab;
  color: #94a3b8;
}

.column-drag-handle:active {
  cursor: grabbing;
}

.column-editor-name {
  flex: 1;
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
}

.column-drag-ghost {
  opacity: 0.45;
  border-color: var(--v-primary-base);
}

.column-editor-footer {
  padding: 12px 14px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-filter-card {
  border-radius: 20px 20px 0 0;
}

.mobile-filter-header {
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-filter-title {
  color: #0f172a;
  font-size: 16px;
  font-weight: 800;
}

@media (max-width: 600px) {
  .product-header {
    flex-wrap: nowrap;
  }

  .product-actions {
    width: auto;
    margin-left: auto;
    gap: 8px;
  }

  .product-actions .new-product-button {
    width: 40px !important;
    min-width: 40px !important;
    max-width: 40px !important;
    height: 40px !important;
    min-height: 40px !important;
    max-height: 40px !important;
    padding: 0 !important;
    border-radius: 50% !important;
    flex: 0 0 40px;
  }

  .product-actions .v-icon {
    margin: 0 !important;
  }

  .product-action-label {
    display: none;
  }

  .search-field {
    width: auto;
    min-width: 0;
    max-width: none;
    flex: 1 1 auto;
  }

  .product-search {
    width: 100%;
    min-width: 0;
    max-width: none;
  }

  .product-filter-trigger {
    flex: 0 0 40px;
  }

  .product-name-cell {
    min-width: 260px;
  }

  .quick-options {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 1px;
  }

  .quick-options .v-btn {
    flex: 1;
  }

  .bulk-selection-toolbar {
    align-items: flex-start;
  }

  .bulk-selection-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .mobile-pagination {
    display: flex;
    justify-content: center;
  }

  .mobile-pagination::v-deep .v-pagination__item,
  .mobile-pagination::v-deep .v-pagination__navigation {
    background: #ffffff !important;
    border: 1px solid #cbd5e1;
    box-shadow: none;
  }

  .mobile-pagination::v-deep .v-pagination__item--active {
    color: var(--v-primary-base) !important;
    border-color: var(--v-primary-base);
    font-weight: 700;
  }
}
</style>
